import React from 'react';
import { Box, Checkbox, Flex, RowKeyAccessor, TableColumnType } from '@fullstory/components';
import { BulkActions } from './BulkActions';
import { BulkAction } from './types';
import { BulkActionResourceType } from '../types';

type RowKeyAccessorReturnType<R> = R[keyof R] | string | number;
function getKey<R>(row: R, rowKeyAccessor: RowKeyAccessor<R>) {
  if (typeof rowKeyAccessor === 'function') {
    return rowKeyAccessor(row);
  }
  return row[rowKeyAccessor];
}

function toggle<R>(set: Set<R>, row: R): boolean {
  if (set.has(row)) {
    set.delete(row);
    return false;
  }
  set.add(row);
  return true;
}

function getFurthest<R>(
  rows: R[],
  rowKeyAccessor: RowKeyAccessor<R>,
  selected: Set<RowKeyAccessorReturnType<R>>,
  start: number,
  asc: boolean,
): number {
  if (asc) {
    for (let i = start; i < rows.length; i++) {
      const r = rows[i];
      if (r && !selected.has(getKey(r, rowKeyAccessor) as RowKeyAccessorReturnType<R>)) {
        return i - 1;
      }
    }
    return rows.length - 1;
  }
  for (let i = start; i >= 0; i--) {
    const r = rows[i];
    if (r && !selected.has(getKey(r, rowKeyAccessor) as RowKeyAccessorReturnType<R>)) {
      return i + 1;
    }
  }
  return 0;
}

function useSelectionState<R>(
  rows: R[],
  rowKeyAccessor: RowKeyAccessor<R>,
  isSelectable: ((row: R) => boolean) | undefined,
  isPaginated: boolean,
) {
  type Key = RowKeyAccessorReturnType<R>;
  interface SelectionState {
    set: Set<Key>;
    setRows: Set<R>;
    anchorIdx: number | undefined;
    all: boolean;
  }

  const [selected, setSelected] = React.useState<SelectionState>({
    set: new Set(),
    setRows: new Set(),
    anchorIdx: undefined,
    all: false,
  });

  const rowToIndex = React.useMemo(
    () => rows.reduce((map: Map<R, number>, row: R, idx: number) => map.set(row, idx), new Map()),
    [rows],
  );

  React.useEffect(() => {
    if (isPaginated) return;
    // Ensure that rows do not remain selected after being filtered out.
    setSelected(prev => {
      const next = new Set<Key>();
      const nextRows = new Set<R>();
      for (const row of rows) {
        const key = getKey(row, rowKeyAccessor);
        if (prev.set.has(key) && isSelectable?.(row)) {
          next.add(key);
          nextRows.add(row);
        }
      }
      return { ...prev, set: next, setRows: nextRows };
    });
  }, [isPaginated, rowKeyAccessor, rows, isSelectable]);

  const onClick = React.useCallback(
    (row: R, e: React.MouseEvent) => {
      const rowIdx = rowToIndex.get(row);
      if (rowIdx === undefined) return;
      setSelected(prev => {
        const all = false; // Clicking a row always takes us out of 'all' mode
        const nextSet = new Set(prev.set);
        const nextSetRows = new Set(prev.setRows);
        const checked = toggle(nextSet, getKey(row, rowKeyAccessor));
        toggle(nextSetRows, row); // to be used for row set
        if (e.shiftKey && prev.anchorIdx !== undefined) {
          const asc = rowIdx > prev.anchorIdx;
          if (checked) {
            // Select all between anchor and row
            const [start, end] = asc ? [prev.anchorIdx, rowIdx] : [rowIdx, prev.anchorIdx];
            for (let i = start; i < end; i++) {
              const r = rows[i];
              if (r) {
                nextSet.add(getKey(r, rowKeyAccessor) as RowKeyAccessorReturnType<R>);
                nextSetRows.add(r);
              }
            }
          } else {
            // Deselect from row to furthest contiguous selected
            const furthest = getFurthest(rows, rowKeyAccessor, prev.set, prev.anchorIdx, asc);
            const [start, end] = asc ? [rowIdx, furthest] : [furthest, rowIdx];
            for (let i = start; i <= end; i++) {
              const r = rows[i];
              if (r) {
                nextSet.delete(getKey(r, rowKeyAccessor) as RowKeyAccessorReturnType<R>);
                nextSetRows.delete(r);
              }
            }
          }

          if (rowIdx === prev.anchorIdx) {
            return { set: nextSet, setRows: nextSetRows, anchorIdx: undefined, all }; // Remove the anchor if it was the clicked row
          }
          return { ...prev, set: nextSet, setRows: nextSetRows, all };
        }

        return {
          set: nextSet,
          setRows: nextSetRows,
          anchorIdx: rowIdx,
          all,
        };
      });
    },
    [rowKeyAccessor, rowToIndex, rows],
  );

  const isChecked = React.useCallback(
    (row: R) => selected.set.has(getKey(row, rowKeyAccessor)) || selected.all,
    [rowKeyAccessor, selected.all, selected.set],
  );

  const togglePage = React.useCallback(
    () =>
      setSelected(prev => {
        const next = new Set<Key>();
        const nextSetRows = new Set<R>();
        const selectableRows = rows.filter(row => isSelectable?.(row) || false);
        if (prev.set.size < selectableRows.length) {
          for (const row of selectableRows) {
            next.add(getKey(row, rowKeyAccessor));
            nextSetRows.add(row);
          }
        }
        return { ...prev, set: next, setRows: nextSetRows, all: false };
      }),
    [isSelectable, rowKeyAccessor, rows],
  );

  const selectAll = () => setSelected(prev => ({ ...prev, all: true }));

  const resetSelected = React.useCallback(
    () =>
      setSelected({
        set: new Set(),
        setRows: new Set(),
        anchorIdx: undefined,
        all: false,
      }),
    [],
  );

  return {
    onClick,
    isChecked,
    togglePage,
    numSelected: selected.set.size,
    selectedRows: selected.setRows,
    allRowsSelected: selected.all,
    selectAll,
    resetSelected,
  };
}

/**
 * Hook to provide the pieces needed for bulk actions to the table
 * and manage state for the currently selected items.
 * This is for the BulkActionTable to use internally.
 *
 * @param resourceType The type of resource to display in the toolbar.
 * @param rows The current set of rows rendered in the table.
 * @param bulkActions Set of bulk actions to display when items are selected. See the provided bulk action hooks like useButtonAction.
 * @param count The total number of items in the table. This is used to determine if all items are selected.
 * @param isSelectable Function to determine if a row is selectable. If undefined, all rows are selectable.
 * @param fixed If true, the toolbar will be fixed to the top of the table.
 * @param simplified If true, the toolbar will be simplified to only show the number of selected items.
 * @param isPaginated If true, the table is paginated. This is used to determine if rows should be deselected when filtered out.
 * @param rowKeyAccessor Function to get the key for a row. This is used to determine if a row is selected.
 * @param testId Test id to apply to the bulk actions toolbar.
 * @param disableSelectAll If true, the "Select All" button will be disabled.
 * @returns Elements to render in the table, including the BulkActions toolbar and column of checkboxes for selection.
 */
export function useBulkActions<R>(
  resourceType: BulkActionResourceType, // TODO this feels shoehorned in. It's only here so it can be drilled down to the BulkActions component. Maybe worth setting up a context for BulkActions to read from.
  testId: string,
  rows: R[],
  rowKeyAccessor: RowKeyAccessor<R>,
  bulkActions: BulkAction<R>[],
  count: number | undefined,
  isSelectable: ((row: R) => boolean) | undefined,
  fixed: boolean | undefined,
  simplified: boolean | undefined,
  isPaginated: boolean,
  disableSelectAll?: boolean,
) {
  const {
    onClick,
    isChecked,
    togglePage,
    numSelected,
    selectedRows,
    allRowsSelected,
    selectAll,
    resetSelected,
  } = useSelectionState(rows, rowKeyAccessor, isSelectable, isPaginated);

  const selectedColumn: TableColumnType<R> = {
    key: 'selected',
    // Make this column as narrow as possible. The built-in table padding will still give it enough room. The exception
    // here is if the table is a fixed width. If it is a fixed width, 52px will give it a good width.
    width: fixed ? 52 : 0,
    // eslint-disable-next-line react/display-name
    renderCell: row => {
      if (isSelectable && !isSelectable(row)) return null;
      return (
        <Checkbox
          testId={`${testId}-select-checkbox`}
          onClick={e => {
            e.stopPropagation();
            onClick(row, e);
          }}
          checked={isChecked(row)}
          disabled={bulkActions.length === 0}
          onChange={() => {}}
          targetSize="large"
        />
      );
    },
    label: simplified ? (
      <Checkbox
        testId={`${testId}-select-all-checkbox`}
        checked={(rows.length > 0 && numSelected === rows.length) || allRowsSelected}
        indeterminate={numSelected > 0 && numSelected < rows.length && !allRowsSelected}
        onClick={e => e.stopPropagation()}
        disabled={bulkActions.length === 0}
        targetSize="large"
        onChange={
          numSelected > 0 && numSelected < rows.length && !allRowsSelected
            ? resetSelected
            : togglePage
        }
      />
    ) : null,
  };

  const bulkActionsElement = (
    <BulkActions
      resourceType={resourceType}
      numSelected={numSelected}
      numRowsOnPage={rows.length}
      totalRows={count || rows.length}
      toggleAllDisabled={bulkActions.length === 0}
      onTogglePage={togglePage}
      allSelected={allRowsSelected}
      onSelectAll={selectAll}
      simplified={simplified}
      testId={testId}
      disableSelectAll={disableSelectAll}
    >
      <Flex gap={12} style={{ paddingLeft: 12 }}>
        {numSelected > 0 &&
          bulkActions.map(bulkAction => {
            const selected = selectedRows;
            const handler = () => {
              bulkAction.action(selected, allRowsSelected);
              resetSelected();
            };
            const elem = bulkAction.render(handler, selected);
            if (elem === null) {
              return null;
            }
            return <Box key={bulkAction.key}>{elem}</Box>;
          })}
      </Flex>
    </BulkActions>
  );

  return { selectedColumn, bulkActionsElement, resetSelected };
}
